import React, { useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { UseControllerProps, useController } from 'react-hook-form';

import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Autocomplete, Box, Chip, IconButton, TextField, Typography } from '@mui/material';

import PriceParrotService from '~/services/PriceParrotService';

import { Property } from '~/types/services/priceParrot';

type LocationAutocompleteProps<T> = {
  control: UseControllerProps<T>['control'];
  name: UseControllerProps<T>['name'];
  label?: string;
  disabled?: boolean;
  rules?: UseControllerProps<T>['rules'];
  onPropertySelect?: (property: Property | null) => void;
};

export default function LocationAutocomplete<T>({
  control,
  name,
  label = 'Property',
  disabled = false,
  rules,
  onPropertySelect,
}: LocationAutocompleteProps<T>) {
  const [loading, setLoading] = useState<boolean>(false);
  const [propertySearchOptions, setPropertySearchOptions] = useState<Array<Property>>([]);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const handleSearchProperties = useMemo(
    () =>
      debounce((searchTerm: string): void => {
        setPropertySearchOptions([]);
        if (searchTerm.length < 2) {
          return;
        }

        setLoading(true);
        PriceParrotService.searchProperty(searchTerm)
          .then(({ data }) => {
            setPropertySearchOptions(data);
          })
          .catch(console.error)
          .finally(() => setLoading(false));
      }, 400),
    [],
  );

  return (
    <Autocomplete
      /* In MUI v5 there doesn't exist renderValue prop, so we need to use multiple + limitTags + renderTags instead*/
      multiple
      limitTags={1}
      renderTags={(value: Array<Property>, getTagProps) => {
        return value[0] ? <Chip label={value[0].name} size="small" {...getTagProps({ index: 0 })} /> : null;
      }}
      freeSolo
      loading={loading}
      options={propertySearchOptions}
      getOptionKey={(option: Property) => option.url}
      getOptionLabel={(option: Property) => option.name}
      /* Disable client side filtering */
      filterOptions={(options) => options}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
            {option.location && (
              <Typography variant="caption" color="textSecondary" noWrap>
                {option.location}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            href={option.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ ml: 1, flexShrink: 0 }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      onInputChange={(_, newInputValue: string, reason: string) => {
        if (reason === 'input') {
          handleSearchProperties(newInputValue);
        } else if (reason === 'clear') {
          setPropertySearchOptions([]);
        }

        field.onChange(newInputValue);
      }}
      onChange={(_, newValue: Array<Property> | null) => {
        const selected = newValue?.[0] ?? null;
        field.onChange(selected?.url ?? '');
        onPropertySelect?.(selected);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          disabled={disabled}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
}
